import { useSyncExternalStore } from 'react';

/**
 * The Explora loader's state, shared from the first line of our code (main.tsx) to the map's first frame: one loader
 * covers the whole load (the route's chunks, the fonts, the map's download and build) and never restarts on the way.
 * `loaded` is the map's chunk having arrived (the loader changes how it moves); `leaving` its fade-out.
 */
export interface ExploraLoaderState {
  active: boolean;
  loaded: boolean;
  leaving: boolean;
}

/** Fade-out of the loader into the page. */
export const LOADER_FADE_MS = 300;
/** Never cover the page for good if it never reports ready. */
export const GIVE_UP_MS = 15_000;

const IDLE: ExploraLoaderState = { active: false, loaded: false, leaving: false };
let state = IDLE;
const listeners = new Set<() => void>();
let giveUp = 0;
let fade = 0;

const set = (next: Partial<ExploraLoaderState>) => {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
};

/** Shows the loader (a no-op while it is already up). */
export function startExploraLoader() {
  if (state.active && !state.leaving) return;
  window.clearTimeout(fade);
  window.clearTimeout(giveUp);
  set({ active: true, leaving: false });
  giveUp = window.setTimeout(finishExploraLoader, GIVE_UP_MS);
}

export function markExploraMapLoaded() {
  if (!state.loaded) set({ loaded: true });
}

/** Fades the loader out once the fonts are in too (the page under it is then the page it will be). */
export function finishExploraLoader() {
  if (!state.active || state.leaving) return;
  const fonts = typeof document !== 'undefined' ? document.fonts?.ready : undefined;
  const leave = () => {
    if (!state.active || state.leaving) return;
    window.clearTimeout(giveUp);
    set({ leaving: true });
    fade = window.setTimeout(() => set({ active: false, leaving: false }), LOADER_FADE_MS);
  };
  if (fonts) fonts.then(leave, leave);
  else leave();
}

export function useExploraLoader() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state
  );
}

export const getExploraLoader = () => state;

/** Tests only. */
export function resetExploraLoader() {
  window.clearTimeout(fade);
  window.clearTimeout(giveUp);
  state = IDLE;
  listeners.forEach((l) => l());
}

const EXPLORA_PATHS = ['/locaciones', '/galeria', '/resenas'];
export const isExploraPath = (pathname: string) =>
  EXPLORA_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
